import { isInternal, isWeb } from '../environment-utils';
import { DebugLog } from '../debug-log';
import {
  getMraidPolyfill,
  getOCSMraidSpecificPayload,
  PolyFillOptions,
  VIEWABLE_CHANGE_STRATEGY
} from './mraid-polyfill';

export type MraidProviderOptions = {
  isMraidCompliant: boolean;
  appExchange?: string;
  dsp?: string;
};

type Window = { mraid?: Partial<Mraid> };

export class MraidProvider {
  async provideMraid(window: Window, options: MraidProviderOptions) {
    await this.initializeMraid(window, options);
    this.polyfillMraidMethodForOCSIfNeeded(window, options);

    const mraid = window.mraid!;

    if (!mraid.__ogyPolyfill) {
      mraid.__ogyPolyfill = false;
    }

    mraid.openNoRedirect = this.createDefaultOpenNoRedirectHandler();
    window.mraid = mraid;
  }

  // Apply the polyfill if the mraid is not loaded or if the polyfill type is full
  private async initializeMraid(
    window: Window,
    options: MraidProviderOptions
  ): Promise<void> {
    const isMraidLibLoaded = typeof window.mraid !== 'undefined';

    if (isMraidLibLoaded) {
      return;
    }

    if (!options.isMraidCompliant) {
      this.polyfill(window, options);
    } else {
      await this.loadMraidWithFallback(window, options);
    }
  }

  private async loadMraidWithFallback(
    window: Window,
    options: MraidProviderOptions
  ): Promise<void> {
    try {
      await this.injectMRAIDScript();
    } catch (error) {
      this.polyfill(window, options);
    }
  }

  private createDefaultOpenNoRedirectHandler() {
    return () => {
      DebugLog.log('openNoRedirect is not ready yet');
    };
  }

  private polyfillMraidMethodForOCSIfNeeded(
    window: Window,
    options: MraidProviderOptions
  ) {
    if (!window.mraid || isWeb()) {
      return;
    }

    const isMraidEditable =
      !Object.isFrozen(window.mraid) && !Object.isSealed(window.mraid);

    const isExternalSSP =
      options.appExchange && options.appExchange.toLowerCase() !== 'ogury';

    const hasPolyfillAlreadyBeenApplied = window.mraid.__ogyPolyfill;

    if (isMraidEditable && isExternalSSP && !hasPolyfillAlreadyBeenApplied) {
      window.mraid.ogySendTrackingEvent = getOCSMraidSpecificPayload().ogySendTrackingEvent;
      window.mraid.__ogyPolyfill = true;
    }
  }

  private polyfill(window: Window, options: MraidProviderOptions) {
    const polyfillOptions: PolyFillOptions = {
      viewableChangeStrategy: VIEWABLE_CHANGE_STRATEGY.BIND_ON_BROWSER_EVENTS
    };

    if (!options.isMraidCompliant && isInternal(options.dsp || '')) {
      polyfillOptions.viewableChangeStrategy =
        VIEWABLE_CHANGE_STRATEGY.ALWAYS_VISIBLE;
    }

    window.mraid = getMraidPolyfill(polyfillOptions);
  }

  protected injectMRAIDScript(): Promise<void> {
    const head = document
      .getElementsByTagName('head')
      .item(0) as HTMLHeadElement;
    const js = document.createElement('script');

    return new Promise<void>((resolve, reject) => {
      js.onerror = err => {
        reject(err);
      };
      js.onload = () => {
        if (window.mraid) resolve();
        else reject();
      };
      js.setAttribute('type', 'text/javascript');
      js.setAttribute('src', 'mraid.js');
      head.appendChild(js);
    });
  }
}
